import {
  Box,
  Button,
  Checkbox,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import {
  Dispatch,
  FC,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from 'react'
import {
  Divider,
  Icons,
  InformationModal,
  ItemImageArea,
  SerialNumber,
} from 'utoniq-ui'

import {
  ItemDetailBoxFragment,
  ItemType,
  UserItemDetailBoxFragment,
} from '@/clients/utoniq-core/schema'
import { ConsoleBaseLayout } from '@/components/layouts/ConsoleBaseLayout'
import { ItemIcon, Loading } from '@/components/organisms'
import { ArtistThemeProvider } from '@/components/styles'
import { useRouter, useTranslation } from '@/core'
import { Template } from '@/core/architecture'

import type { Action, State } from '../viewModel'
export * from './fragments'

export const ConsoleUserOwnedItemDetailTemplate: Template<State, Action> = ({
  state,
  onUseTicketsBluk,
}) => {
  const { t } = useTranslation('consoleUserOwnedItemDetail')
  const [editable, toggleEditable] = useReducer(v => !v, false)
  const [modalOpen, toggleModalOpen] = useReducer(v => !v, false)
  const [selectedSerialIds, setSelectedSerialIds] = useState<string[]>(
    state.initialSelectedSerialIds
  )
  const { routerPages, generatePath, router } = useRouter<{ userId: string }>()

  const isUsed = useMemo(() => {
    if (state.throughTicketIndex) {
      return !state.portfolio?.serials?.filter(
        ({ serial }) =>
          state.throughTicketIndex &&
          !serial?.ticketStatus?.throughTicket?.[+state.throughTicketIndex]
            ?.isUsed
      ).length
    }
    return !state.portfolio?.serials?.filter(
      ({ serial }) => !serial?.ticketStatus?.isUsed
    ).length
  }, [state.portfolio?.serials, state.throughTicketIndex])

  useEffect(() => {
    setSelectedSerialIds(state.initialSelectedSerialIds)
  }, [state.initialSelectedSerialIds])

  return (
    <ArtistThemeProvider artistId={state.artistId}>
      <ConsoleBaseLayout
        header={{
          back: true,
          defaultBackPage: generatePath(routerPages.ConsoleUserOwnedItems, {
            userId: router.query.userId ?? '#',
          }),
          title: t('title', '所有アイテム詳細'),
        }}
        sx={{
          p: 5,
        }}
        customNavigation={
          editable ? (
            <Box
              sx={{
                bgcolor: 'grey.50',
                height: '76px',
                display: 'grid',
                placeItems: 'center',
              }}
            >
              <Button
                disabled={
                  selectedSerialIds.toString() ===
                  state.initialSelectedSerialIds.toString()
                }
                onClick={toggleModalOpen}
                size="large"
                sx={{ width: 212 }}
              >
                {t('changeStatus', '使用状況を変更する')}
              </Button>
            </Box>
          ) : null
        }
      >
        <TitleCell portfolio={state.portfolio} />
        <ItemCell
          item={state.portfolio?.item}
          isUsed={isUsed}
          thoughTicket={state.thoughTicket}
        />
        {state.loading ? (
          <Loading />
        ) : (
          <TicketsCell
            portfolio={state.portfolio}
            editable={editable}
            toggleEditable={toggleEditable}
            modalOpen={modalOpen}
            toggleModalOpen={toggleModalOpen}
            onUseTicketsBluk={onUseTicketsBluk}
            submitting={state.submitting}
            throughTicketIndex={state.throughTicketIndex}
            selectedSerialIds={selectedSerialIds}
            setSelectedSerialIds={setSelectedSerialIds}
          />
        )}
      </ConsoleBaseLayout>
    </ArtistThemeProvider>
  )
}

type TitleCellProps = {
  portfolio: State['portfolio']
}
const TitleCell: FC<TitleCellProps> = ({ portfolio }) => {
  return (
    <Box>
      <Typography variant="h3">
        {portfolio?.item.preSale?.event?.title}
      </Typography>
      <Typography variant="body2" sx={{ mt: 2 }}>
        {portfolio?.item.preSale?.event?.description}
      </Typography>
    </Box>
  )
}

type ItemCell = {
  item?: ItemDetailBoxFragment
  thoughTicket: State['thoughTicket']
  isUsed: boolean
}
const ItemCell: FC<ItemCell> = ({ item, isUsed, thoughTicket }) => {
  const { t } = useTranslation('consoleUserOwnedItemDetail')
  const { generatePath, routerPages } = useRouter()

  if (!item) return null

  return (
    <Stack sx={{ flexDirection: 'row', gap: 4, mt: 4, px: 3 }}>
      <Box sx={{ position: 'relative' }}>
        <Stack
          sx={{
            zIndex: 1,
            marginInline: 'auto',
            pl: 1,
            width: 104,
            position: 'absolute',
            top: 8,
          }}
        >
          <ItemIcon type={item.type} />
        </Stack>
        <ItemImageArea
          size="small"
          src={item.thumbnail?.front?.smallUri ?? ''}
          href={generatePath(routerPages.ConsoleItemDetail, {
            itemId: item.id ?? '#',
          })}
        />
      </Box>
      <Box>
        <Stack flex={1} justifyContent="center">
          <Stack className="item-info" sx={{ flexDirection: 'row', gap: 2 }}>
            {item.type === ItemType.Ticket && (
              <Chip
                color={isUsed ? 'default' : 'primary'}
                label={
                  <Typography
                    variant="overline"
                    color={isUsed ? 'text.secondary' : 'white'}
                  >
                    {isUsed
                      ? t('statusused', '使用済')
                      : t('statusunused', '未使用')}
                  </Typography>
                }
                sx={{
                  p: 1,
                  height: 20,
                  '.MuiChip-label': { px: 0 },
                }}
              />
            )}
            {item.metadata?.ticket?.isThroughTicket && (
              <Chip
                color={'secondary'}
                label={
                  <Typography variant="overline" color={'white'}>
                    {t('throughTicket', '通し券')}
                  </Typography>
                }
                sx={{
                  p: 1,
                  height: 20,
                  '.MuiChip-label': { px: 0 },
                }}
              />
            )}
          </Stack>
        </Stack>
        <Typography variant="subtitle1" sx={{ mt: 2 }}>
          {item.title} {thoughTicket?.name}
        </Typography>
        <Typography variant="caption" sx={{ mt: 2 }}>
          {item.sku?.unitName}
        </Typography>
      </Box>
    </Stack>
  )
}

type TicketsCellProps = {
  portfolio: State['portfolio']
  throughTicketIndex: State['throughTicketIndex']
  editable: boolean
  toggleEditable: () => void
  modalOpen: boolean
  toggleModalOpen: () => void
  onUseTicketsBluk: Action['onUseTicketsBluk']
  submitting: boolean
  selectedSerialIds: string[]
  setSelectedSerialIds: Dispatch<SetStateAction<string[]>>
}
const TicketsCell: FC<TicketsCellProps> = ({
  portfolio,
  throughTicketIndex,
  editable,
  toggleEditable,
  modalOpen,
  toggleModalOpen,
  onUseTicketsBluk,
  submitting,
  selectedSerialIds,
  setSelectedSerialIds,
}) => {
  const { t } = useTranslation('consoleUserOwnedItemDetail')
  const unselectedSerialIds = useMemo(
    () =>
      portfolio?.serials
        ?.filter(({ id }) => !selectedSerialIds.includes(id))
        ?.map(serial => serial.id) ?? [],
    [portfolio, selectedSerialIds]
  )

  const { used, total } = useMemo(() => {
    if (throughTicketIndex) {
      return {
        used: portfolio?.serials?.filter(
          ({ serial }) =>
            throughTicketIndex &&
            !serial?.ticketStatus?.throughTicket?.[+throughTicketIndex]?.isUsed
        ).length,
        total: portfolio?.serials?.length,
      }
    }
    return {
      used: portfolio?.serials?.filter(({ isUsed }) => !isUsed)?.length,
      total: portfolio?.serials?.length,
    }
  }, [portfolio?.serials, throughTicketIndex])

  const addSerialId = useCallback((serialId: string) => {
    setSelectedSerialIds(prev => [...prev, serialId])
  }, [])

  const removeSerialId = useCallback((serialId: string) => {
    setSelectedSerialIds(prev => prev.filter(id => id !== serialId))
  }, [])

  const checkTicketUsed = useCallback(
    (serial: UserItemDetailBoxFragment['serials'][number]) => {
      if (throughTicketIndex) {
        return !!serial.serial?.ticketStatus?.throughTicket?.[
          +throughTicketIndex
        ]?.isUsed
      }
      return !!serial.serial?.ticketStatus?.isUsed
    },
    [throughTicketIndex]
  )
  const isThroughTicket = portfolio?.item.metadata?.ticket?.isThroughTicket

  return (
    <>
      <Box sx={{ mt: 6, pb: '60px' }}>
        <Typography variant="h6" color="text.secondary">
          {t('ownedTickets', '所有チケット')}（{t('statusused', '未使用')}：
          {used}/{total}枚）
        </Typography>

        <Stack justifyContent="flex-end" alignItems="flex-end">
          <Button variant="text" fullWidth={false} onClick={toggleEditable}>
            {editable ? '' : t('editStatus', '使用状況を編集する')}
          </Button>
        </Stack>
        <TableContainer>
          <Table
            aria-label="user-tickets-table"
            sx={{
              width: 'auto',
              ovrerflowX: 'scroll',
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell sx={{ border: 'none', minWidth: 52, py: 2 }}>
                  <Typography
                    variant="overline"
                    color="text.hint"
                    sx={{ display: 'block' }}
                    textAlign="center"
                  >
                    {t('isUsed', '使用\n状況')}
                  </Typography>
                </TableCell>
                <TableCell sx={{ border: 'none', minWidth: 96, py: 2, px: 2 }}>
                  <Typography
                    variant="overline"
                    color="text.hint"
                    sx={{ display: 'block' }}
                  >
                    {t('serialNumber', 'シリアルナンバー')}
                  </Typography>
                </TableCell>
                <TableCell
                  align="left"
                  sx={{ border: 'none', minWidth: 88, py: 2, px: 3 }}
                >
                  <Typography variant="overline" color="text.hint">
                    {t('assignedNumber', '整理番号')}
                  </Typography>
                </TableCell>
                <TableCell
                  align="left"
                  sx={{ border: 'none', minWidth: 88, py: 2, px: 3 }}
                >
                  <Typography variant="overline" color="text.hint">
                    {t('usedAt', '使用日')}
                  </Typography>
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {portfolio?.serials?.map((serial, index) => {
                const isUsed = checkTicketUsed(serial)
                const isActive = editable
                  ? selectedSerialIds.includes(serial.id)
                  : isUsed

                return (
                  <TableRow key={serial.id}>
                    <TableCell
                      padding="none"
                      sx={{
                        py: 2,
                        px: 0,
                        minWidth: 52,
                        border: 'none',
                      }}
                    >
                      <Stack
                        direction="row"
                        justifyContent="center"
                        alignItems="center"
                        sx={{
                          width: 64,
                          '.Mui-checked svg.MuiSvgIcon-root': {
                            color: 'primary.main',
                          },
                        }}
                      >
                        {editable && (
                          <Checkbox
                            sx={{ py: 0 }}
                            checked={isActive}
                            onChange={() => {
                              isActive
                                ? removeSerialId(serial.id)
                                : addSerialId(serial.id)
                            }}
                          />
                        )}
                        {!editable && (
                          <>
                            {isUsed ? (
                              <Icons.Check
                                color="primary"
                                sx={{ mx: 'auto' }}
                                fontSize="small"
                              />
                            ) : (
                              <Typography
                                variant="overline"
                                align="center"
                                sx={{ px: 2, width: '100%' }}
                              >
                                {'-'}
                              </Typography>
                            )}
                          </>
                        )}
                        <Divider
                          orientation="vertical"
                          flexItem
                          sx={{ height: 20 }}
                        />
                      </Stack>
                    </TableCell>
                    <TableCell
                      padding="none"
                      sx={{
                        py: 2,
                        pl: 3,
                        pr: 0,
                        border: 'none',
                      }}
                    >
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        sx={{ pr: 1 }}
                      >
                        <SerialNumber label={serial.formattedSerialNumber} />
                      </Stack>
                    </TableCell>
                    <TableCell
                      padding="none"
                      sx={{
                        py: 2,
                        px: 0,
                        border: 'none',
                      }}
                    >
                      <Stack direction="row" spacing={2} alignItems="center">
                        <Divider
                          orientation="vertical"
                          flexItem
                          sx={{ height: 20 }}
                        />
                        <Typography variant="overline">
                          {throughTicketIndex && isThroughTicket
                            ? serial.serial?.ticketStatus?.throughTicket?.[
                                +throughTicketIndex
                              ]?.seat
                            : serial?.serial?.formattedAssignedNumber ?? '-'}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell
                      padding="none"
                      sx={{
                        py: 2,
                        px: 0,
                        border: 'none',
                      }}
                    >
                      <Stack direction="row" spacing={2} alignItems="center">
                        <Divider
                          orientation="vertical"
                          flexItem
                          sx={{ height: 20 }}
                        />
                        <Typography variant="overline">
                          {serial.serial?.ticketStatus?.formattedUsedAt ?? '-'}
                        </Typography>
                      </Stack>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <InformationModal
        open={modalOpen}
        onClose={toggleModalOpen}
        title={t('confirmStatusChange', '使用状況を修正しますか？')}
        content={
          <Box sx={{ py: 5, textAlign: 'left' }}>
            {!!selectedSerialIds.length && (
              <>
                <Typography>{t('used', '使用済み')}</Typography>
                <Typography sx={{ whiteSpace: 'pre-wrap' }}>
                  {portfolio?.serials
                    .filter(({ id }) => selectedSerialIds.includes(id))
                    .map(({ serial, formattedSerialNumber }) =>
                      isThroughTicket && throughTicketIndex
                        ? serial?.ticketStatus?.throughTicket?.[
                            +throughTicketIndex
                          ]?.seat
                        : serial?.formattedAssignedNumber ??
                          formattedSerialNumber
                    )
                    .join(', ')}
                </Typography>
              </>
            )}
            {!!unselectedSerialIds.length && (
              <>
                <Typography>{t('unused', '未使用')}</Typography>
                <Typography sx={{ whiteSpace: 'pre-wrap' }}>
                  {portfolio?.serials
                    .filter(({ id }) => unselectedSerialIds.includes(id))
                    .map(({ serial, formattedSerialNumber }) =>
                      isThroughTicket && throughTicketIndex
                        ? serial?.ticketStatus?.throughTicket?.[
                            +throughTicketIndex
                          ]?.seat
                        : serial?.formattedAssignedNumber ??
                          formattedSerialNumber
                    )
                    .join(', ')}
                </Typography>
              </>
            )}
          </Box>
        }
        buttons={
          <Button
            color="primary"
            sx={{ minWidth: 212 }}
            loading={submitting}
            size="large"
            onClick={() => {
              onUseTicketsBluk(selectedSerialIds, unselectedSerialIds).finally(
                () => {
                  toggleModalOpen()
                  toggleEditable()
                }
              )
            }}
          >
            OK
          </Button>
        }
        cancelText={t('close', '閉じる')}
      />
    </>
  )
}
